import { Trans, useTranslation } from 'next-i18next'

import {
  Heading3,
  LinkItem,
  LinkList,
  LinkText,
  Paragraph,
  Section3
} from '@/features/Listings/seo/elements'
import AverageRentFaq from '@/features/Listings/seo/search-results-content/content/average-rent-faq'
import ContentTemplate from '@/features/Listings/seo/search-results-content/content/city/content-template'
import getFilterLinks from '@/features/Listings/seo/search-results-content/content/get-filter-links'

import Amenities from '../amenities'
import RentingIn from '../renting-in'

const Duncan = ({
  filterSlug,
  isCanadaPage,
  cityName,
  cityNameWithState,
  cityData,
  isChinese
}) => {
  const { t } = useTranslation()
  const { state, slug: citySlug } = cityData
  const links = getFilterLinks(isCanadaPage, citySlug)

  const BcNeighbourhoodsLink =
    'https://liv.rent/blog/neighbourhoods/british-columbia-neighbourhoods/'

  const cheapNeighbourhoodsLink = isChinese
    ? 'https://liv.rent/blog/zh/%e8%a1%97%e5%8c%ba%e6%8c%87%e5%8d%97/%e7%a7%9f%e6%88%bf%e5%ae%9d%e5%85%b8-%e5%a4%a7%e6%b8%a9%e5%93%a5%e5%8d%8e%e7%a7%9f%e9%87%91%e6%9c%80%e4%be%bf%e5%ae%9c%e7%9a%8410%e4%b8%aa%e5%9c%b0%e5%8c%ba/'
    : 'https://liv.rent/blog/neighbourhoods/2020-renting-in-canada-what-1800-gets-you-across-canada/'

  const getAbout = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.city.duncan-bc:About Duncan Paragraph 1">
          Duncan, nestled within the picturesque Cowichan Valley on Vancouver
          Island, exemplifies the cultural depth and natural beauty found in{' '}
          <LinkText href="/rental-listings/british-columbia">
            British Columbia (BC)
          </LinkText>
          . The &quot;City of Totems&quot; boasts a rich Indigenous heritage,
          showcased by its impressive collection of totem poles, while the
          Cowichan Tribes contribute to the area’s cultural vibrancy. This
          region, often referred to as the &quot;Napa of the North,&quot;
          thrives on its wine industry and diverse agriculture, enhanced by a
          mild climate. Outdoor enthusiasts revel in opportunities for hiking,
          kayaking, and fishing, with the Cowichan River and nearby Cowichan
          Lake adding to the allure. From art galleries to farmers’ markets,
          Duncan offers an array of cultural and recreational experiences that
          celebrate the heart of BC’s beauty and community spirit within its{' '}
          <LinkText href={BcNeighbourhoodsLink}>
            diverse cities and neighbourhoods
          </LinkText>
          .
        </Trans>
      </Paragraph>
    )
  }

  const getRentingIn = () => {
    return (
      <RentingIn
        filterSlug={filterSlug}
        cityName={cityName}
        rentingTipsLink={cheapNeighbourhoodsLink}
      />
    )
  }

  const getAverageRent = () => {
    return (
      <AverageRentFaq
        filterSlug={filterSlug}
        isCanadaPage={isCanadaPage}
        cityName={cityName}
        cityNameWithState={cityNameWithState}
        citySlug={citySlug}
        rentAmounts={{
          bed1: '$1,200 - $2,200',
          bed2: '$1,600 - $2,800',
          bed3: '$2,300 - $3,500'
        }}
        rentReportCity={t('cities:city_name_vancouver')}
        rentReportLink="https://liv.rent/blog/rent-reports/vancouver/"
        rentReportLinkZh="https://liv.rent/blog/zh/%E7%A7%9F%E9%87%91%E7%BB%9F%E8%AE%A1/%E6%B8%A9%E5%93%A5%E5%8D%8E/"
      />
    )
  }

  const getApartmentHunting = () => {
    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo.city.duncan-bc:Apartment Hunting in Duncan Paragraph">
            Compared to cities like{' '}
            <LinkText href="/rental-listings/city/vancouver">
              Vancouver
            </LinkText>{' '}
            and{' '}
            <LinkText href="/rental-listings/city/richmond">Richmond</LinkText>,
            it’s easier to find{' '}
            <LinkText href={links.under1500}>cheap rentals in Duncan</LinkText>{' '}
            – although{' '}
            <LinkText href={links.under1000}>
              apartments for rent under $1000
            </LinkText>{' '}
            are still uncommon.
          </Trans>
        </Paragraph>
        <Paragraph>
          <Trans i18nKey="seo:Apartment Hunting in LOCATION Paragraph 2">
            In addition to choosing your location, it’s important to know what
            rental types are available in {{ cityName }} (e.g.{' '}
            <LinkText href={links.bed1}>1 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed2}>2 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed3}>3 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.studio}>studio apartments</LinkText>) and the
            average rent for each property type. Refer below to figure out your
            options - ranging from{' '}
            <LinkText href={links.furnished}>furnished rentals</LinkText> and{' '}
            <LinkText href={links.unfurnished}>unfurnished rentals</LinkText> to{' '}
            <LinkText href={links.longTerm}>long term rentals</LinkText> and{' '}
            <LinkText href={links.shortTerm}>short term rentals</LinkText>.
          </Trans>
        </Paragraph>
      </>
    )
  }

  const getRentalTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Apartments for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Apartments for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.duncan-bc:Apartments for rent Duncan Paragraph">
              <LinkText href={links.apartment}>
                Apartments for rent in Duncan
              </LinkText>{' '}
              are popular among singles, couples, and small families, offering a
              practical and low-maintenance living option. These units range
              from older, more affordable buildings to newer, modern complexes.
              Many apartments are conveniently located near the city’s
              amenities, such as shops, restaurants, and parks, making them
              ideal for those who value ease of access to urban conveniences.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Houses for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Houses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.duncan-bc:Houses for rent Duncan Paragraph">
              <LinkText href={links.house}>Renting a house in Duncan</LinkText>{' '}
              is a preferred choice for families or individuals seeking more
              space and privacy. These homes often feature yards and gardens,
              providing an ideal setting for outdoor activities and relaxation.
              The range of houses for rent in Duncan includes charming historic
              homes as well as newer constructions, catering to a variety of
              tastes and needs.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Townhouses for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Townhouses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.duncan-bc:Townhouses for rent Duncan Paragraph">
              <LinkText href={links.townhouse}>
                Townhouses for rent in Duncan
              </LinkText>{' '}
              provide a balance between the community feel of an apartment
              complex and the privacy of a detached home. They are commonly
              chosen by small families, couples, or professionals. These
              multi-level units often come with their own small yards or patios
              and are part of developments that foster a sense of community
              among residents.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Basements for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Basements for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.duncan-bc:Basements for rent Duncan Paragraph">
              Basements for rent in Duncan offer an affordable alternative for
              renters, such as students, singles, or young professionals. These
              units, typically part of larger houses, provide essential living
              spaces at a lower cost. While they may have some limitations in
              terms of natural light and space, they offer a more private and
              cozy living environment
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Condos for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Condos for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.duncan-bc:Condos for rent Duncan Paragraph">
              <LinkText href={links.apartment}>
                Condos for rent in Duncan
              </LinkText>{' '}
              are the most budget-friendly option and is popular among students,
              seasonal workers, or individuals looking for flexible living
              arrangements. Rooms for rent involve sharing common areas such as
              kitchens and bathrooms with other occupants and can offer a social
              and communal living experience.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Rooms for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Rooms for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.duncan-bc:Rooms for rent Duncan Paragraph">
              <LinkText href={links.room}>Renting a room in Duncan</LinkText> is
              the most budget-friendly option and is popular among students,
              seasonal workers, or individuals looking for flexible living
              arrangements. Rooms for rent involve sharing common areas such as
              kitchens and bathrooms with other occupants and can offer a social
              and communal living experience.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getSpecialTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Pet friendly rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Pet friendly rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Pet friendly rentals CITY Paragraph">
              Though not every landlord is keen on allowing pets, there are a
              good number of{' '}
              <LinkText href={links.petFriendly}>
                pet-friendly rentals in {{ cityName }}
              </LinkText>{' '}
              for those with furry friends. Both{' '}
              <LinkText href={links.dogFriendly}>dog-friendly</LinkText> and{' '}
              <LinkText href={links.catFriendly}>cat-friendly</LinkText> rental
              units are available, as well as properties where both are allowed.
              Keep in mind that some {{ cityName }} neighbourhoods are better
              suited to those with pets, and adjust your search accordingly.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Furnished rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Furnished rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Furnished rentals LOCATION Paragraph">
              If you’re new to town or just haven’t had a chance to pick up
              furniture of your own yet, not to worry – there are plenty of{' '}
              <LinkText href={links.furnished}>furnished rentals</LinkText>{' '}
              available in {{ cityName }}. Renters seeking furnished units
              should search for these units using filters, but do be prepared to
              pay more in rent. There are{' '}
              <LinkText href="https://liv.rent/blog/renters/renting-furnished-unfurnished/">
                pros and cons to renting furnished units compared to unfurnished
                units
              </LinkText>
              , so deciding whether it’s worth it to buy furniture when moving
              is a good first step.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Short term rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Short term rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.duncan-bc:Short term rentals Duncan Paragraph">
              Duncan, known for its cultural heritage and local economy centered
              around retail, services, and tourism, offers various employment
              opportunities. While it may not be particularly noted for its
              academic institutions, it does provide a sense of community and
              lifestyle that might appeal to some looking for short-term
              rentals. The demand for{' '}
              <LinkText href={links.shortTerm}>short-term rentals</LinkText>{' '}
              tends to increase in many urban areas, especially during tourist
              seasons and at the start of school terms, but this trend can be
              less pronounced in smaller cities like Duncan. The flexibility of
              short-term rentals is a significant consideration for many
              renters, and it is an important factor in determining if{' '}
              <LinkText href="https://liv.rent/blog/landlords/short-term-vs-long-term-rentals-which-are-right-for-you/">
                short-term rentals are the right fit for their needs
              </LinkText>
              .
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Cheap rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Cheap rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.duncan-bc:Cheap rentals Duncan Paragraph">
              If you’re looking for{' '}
              <LinkText href={links.under1000}>
                cheap rentals in Duncan
              </LinkText>
              , try finding a roommate or two and explore shared housing; or
              search for <LinkText href={links.room}>rooms for rent</LinkText>.
              It’s worth noting that in Duncan, there are neighbourhoods where
              rent is considerably cheaper, so exploring these options is a good
              idea as well.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getLiving = () => {
    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo.city.duncan-bc:Living in Duncan Paragraph">
            Living in Duncan, British Columbia, offers a similar blend of scenic
            beauty and small-town charm. Situated in the southern part of the
            province, Duncan is surrounded by captivating landscapes, with
            picturesque mountains, serene lakes, and lush forests. The community
            in Duncan is known for its welcoming and friendly atmosphere,
            providing a wide range of amenities for both residents and visitors.
            Whether you’re an outdoor enthusiast looking for thrilling
            adventures like hiking and skiing, or someone who enjoys immersing
            themselves in the arts and culture scene at local museums and
            galleries, Duncan has something for everyone. With its pristine and
            secure environment, Duncan offers an idyllic and peaceful living
            experience, perfect for those seeking tranquility.
          </Trans>
        </Paragraph>
        <Paragraph>
          <Trans i18nKey="seo.city.duncan-bc:Living in Duncan Paragraph 2">
            If you’re not sure that Duncan is the right city for you, check out
            some of these resources comparing the different cities.
          </Trans>
        </Paragraph>
        {isChinese ? (
          <LinkList>
            <LinkItem href="https://liv.rent/blog/zh/%e7%a7%9f%e5%ae%a2/%e5%8a%a0%e6%8b%bf%e5%a4%a7%e7%95%99%e5%ad%a65%e5%a4%a7%e5%9f%8e%e5%b8%82-%e7%a7%9f%e6%88%bf%e3%80%81%e5%ad%a6%e8%b4%b9%e3%80%81%e5%b7%a5%e4%bd%9c%e3%80%81%e7%ad%be%e8%af%81%e8%af%a6%e8%a7%a3/">
              加拿大最佳移民城市
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/zh/%e7%a7%9f%e5%ae%a2/%e6%b8%a9%e5%93%a5%e5%8d%8e%e3%80%81%e5%a4%9a%e4%bc%a6%e5%a4%9a%e6%af%94%e8%be%83/">
              温哥华 vs 多伦多：生活成本比较
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/zh/%E8%A1%97%E5%8C%BA%E6%8C%87%E5%8D%97/%E6%B8%A9%E5%93%A5%E5%8D%8E%E5%92%8C%E7%B4%A0%E9%87%8C%E5%B1%85%E4%BD%8F%E6%88%90%E6%9C%AC%E6%AF%94%E8%BE%83/">
              温哥华 vs 素里：生活成本比较
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/zh/toronto-zh/2021%e7%89%88%e5%a4%9a%e4%bc%a6%e5%a4%9a%e3%80%81%e8%92%99%e7%89%b9%e5%88%a9%e5%b0%94%e7%94%9f%e6%b4%bb%e6%88%90%e6%9c%ac%e6%af%94%e8%be%83/">
              多伦多 vs 蒙特利尔：生活成本比较
            </LinkItem>
          </LinkList>
        ) : (
          <LinkList>
            <LinkItem href="https://liv.rent/blog/2020/11/best-canadian-cities-for-immigration/">
              Best Canadian cities for immigration
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/2019/11/vancouver-or-toronto-a-cost-of-living-comparison/">
              Vancouver vs Toronto: Cost of living comparison
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/2021/02/surrey-vs-vancouver-bc-comparison/">
              Vancouver vs Surrey: Cost of living comparison
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/2021/04/toronto-vs-montreal-cost-of-living-comparison/">
              Toronto vs Montreal: Cost of living comparison
            </LinkItem>
          </LinkList>
        )}
      </>
    )
  }

  const getPopularHoods = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.city.duncan-bc:Popular Neighbourhoods Duncan Paragraph">
          Like any city, Duncan is made up of smaller neighbourhoods with their
          own distinct features. Some of the most popular neighbourhoods include
          Downtown Duncan, Cowichan Bay, East Duncan, West Duncan, Maple Bay,
          Glenora, Somenos, Duncan Rural, Mount Prevost, and Queen Street. For
          more info about Duncan, you can consult this guide on{' '}
          <LinkText href={BcNeighbourhoodsLink}>BC neighbourhoods</LinkText>.
        </Trans>
      </Paragraph>
    )
  }

  const getLandmarks = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.city.duncan-bc:Local Landmarks Duncan Paragraph">
          Duncan, BC, is a picturesque town that boasts a variety of remarkable
          landmarks, showcasing the region’s natural wonders and cultural
          significance. One such landmark is the Cowichan Valley Museum, a
          captivating destination that delves into the town’s rich history and
          showcases its vibrant heritage. The beautiful Quw’utsun’ Cultural and
          Conference Centre provides a unique opportunity to learn about the
          local Indigenous culture through exhibits and interactive experiences.
          For those seeking outdoor adventure, the Mount Tzouhalem Ecological
          Reserve offers breathtaking views of the surrounding landscapes.
          Additionally, the Merridale Cidery & Distillery provides a taste of
          Duncan’s thriving cider and spirits scene. These landmarks contribute
          to the town’s charm and make Duncan an alluring destination for
          visitors.
        </Trans>
      </Paragraph>
    )
  }

  const content = {
    about: getAbout(),
    rentingIn: getRentingIn(),
    averageRent: getAverageRent(),
    apartmentHunting: getApartmentHunting(),
    rentalTypes: getRentalTypes(),
    specialTypes: getSpecialTypes(),
    amenities: (
      <Amenities cityName={cityName} filterSlug={filterSlug} links={links} />
    ),
    living: getLiving(),
    popularHoods: getPopularHoods(),
    landmarks: getLandmarks()
  }

  return (
    <ContentTemplate
      content={content}
      filterSlug={filterSlug}
      isCanadaPage={isCanadaPage}
      cityName={cityName}
      cityNameWithState={cityNameWithState}
      citySlug={citySlug}
      state={state}
    />
  )
}

export default Duncan
